import { useState, useEffect } from "react";
import { trpc } from "@/lib/trpc";
import { SiteLogo } from "@/components/SiteLogo";
import { SITE_NAME, SITE_TAGLINE } from "@shared/site";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Link } from "wouter";
import { CheckCircle, ArrowLeft, Music } from "lucide-react";

const STATION_OPTIONS = [
  "The Pickle Jar — Eclectic",
  "Rock the Court — Classic Rock",
  "Dink & Twang — Country",
  "Dink Don't Stop — Hip-Hop / R&B",
  "Pop & Dink — Pop / Indie",
  "Smash & Drop — Electronic / EDM",
  "Grace & Dink — Christian",
  "Wind Down — Meditation / Hypnotic",
  "Any station",
];

export default function SubmitMusic() {
  const [form, setForm] = useState({
    djName: "",
    email: "",
    genre: "",
    trackTitle: "",
    trackUrl: "",
    station: "",
    notes: "",
  });
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    const prev = document.title;
    document.title = `Submit Music — ${SITE_NAME}`;
    return () => {
      document.title = prev;
    };
  }, []);

  const send = trpc.forms.submitMusic.useMutation({
    onSuccess: () => setSubmitted(true),
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    send.mutate({
      djName: form.djName,
      email: form.email,
      genre: form.genre,
      trackTitle: form.trackTitle,
      trackUrl: form.trackUrl,
      station: form.station || undefined,
      notes: form.notes || undefined,
    });
  };

  const set = (field: keyof typeof form) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => setForm(prev => ({ ...prev, [field]: e.target.value }));

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Header */}
      <header className="border-b border-border/50 bg-background/95 backdrop-blur-md sticky top-0 z-40">
        <div className="container flex items-center gap-3 h-14">
          <Link href="/">
            <button className="flex items-center gap-1.5 text-muted-foreground hover:text-foreground transition-colors text-sm font-medium">
              <ArrowLeft className="w-4 h-4" />
              Back
            </button>
          </Link>
          <span className="text-border/50">|</span>
          <Link href="/" className="text-foreground hover:opacity-90 transition-opacity flex items-center gap-2">
            <SiteLogo className="h-8" />
            <span className="sr-only">
              {SITE_NAME} — {SITE_TAGLINE}
            </span>
          </Link>
        </div>
      </header>

      <main className="container py-12 max-w-xl mx-auto">
        {submitted ? (
          <div className="flex flex-col items-center gap-5 py-16 text-center">
            <CheckCircle className="w-14 h-14" style={{ color: "var(--pb-green)" }} />
            <h2
              className="text-3xl font-black tracking-tight"
              style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
            >
              SUBMISSION RECEIVED!
            </h2>
            <p className="text-muted-foreground">
              Thanks, <strong>{form.djName}</strong>! We'll review your track and be in touch at{" "}
              <strong>{form.email}</strong>.
            </p>
            <Link href="/">
              <Button
                className="mt-2 font-bold text-black"
                style={{ background: "var(--pb-green)" }}
              >
                Back to Radio
              </Button>
            </Link>
          </div>
        ) : (
          <>
            <div className="mb-8">
              <div className="flex items-center gap-2 mb-2">
                <Music className="w-5 h-5" style={{ color: "var(--pb-green)" }} />
                <span
                  className="text-xs font-bold tracking-widest uppercase"
                  style={{ color: "var(--pb-green)" }}
                >
                  DJ Submissions
                </span>
              </div>
              <h1
                className="text-4xl font-black tracking-tight mb-2"
                style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
              >
                SUBMIT YOUR MUSIC
              </h1>
              <p className="text-muted-foreground text-sm leading-relaxed">
                Are you a DJ or artist who wants to be featured on KPKL Radio? Share a link to your
                track and we'll review it for playlist consideration.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-1.5">
                <Label htmlFor="djName">DJ / Artist Name *</Label>
                <Input
                  id="djName"
                  required
                  placeholder="Your DJ or artist name"
                  value={form.djName}
                  onChange={set("djName")}
                  maxLength={120}
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  required
                  placeholder="you@example.com"
                  value={form.email}
                  onChange={set("email")}
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="genre">Genre / Style *</Label>
                <Input
                  id="genre"
                  required
                  placeholder="e.g. Pop, EDM, Classic Rock, Country..."
                  value={form.genre}
                  onChange={set("genre")}
                  maxLength={80}
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="trackTitle">Track Title *</Label>
                <Input
                  id="trackTitle"
                  required
                  placeholder="Name of the track"
                  value={form.trackTitle}
                  onChange={set("trackTitle")}
                  maxLength={200}
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="trackUrl">Track Link * (SoundCloud, Spotify, Mixcloud, etc.)</Label>
                <Input
                  id="trackUrl"
                  type="url"
                  required
                  placeholder="https://soundcloud.com/yourtrack"
                  value={form.trackUrl}
                  onChange={set("trackUrl")}
                  maxLength={500}
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="station">Preferred Station (optional)</Label>
                <select
                  id="station"
                  value={form.station}
                  onChange={set("station")}
                  className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                >
                  <option value="">Select a station...</option>
                  {STATION_OPTIONS.map(s => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="notes">Additional Notes (optional)</Label>
                <Textarea
                  id="notes"
                  placeholder="Anything else you'd like us to know about your track or style..."
                  value={form.notes}
                  onChange={set("notes")}
                  maxLength={2000}
                  rows={4}
                />
              </div>

              {send.error && (
                <p className="text-sm text-destructive">
                  Something went wrong. Please try again.
                </p>
              )}

              <Button
                type="submit"
                disabled={send.isPending}
                className="w-full font-bold text-black"
                style={{ background: "var(--pb-green)" }}
              >
                {send.isPending ? "Sending…" : "Submit Track"}
              </Button>
            </form>
          </>
        )}
      </main>
    </div>
  );
}
